import React, { ReactElement, useState } from 'react';
import { graphql, PageProps } from 'gatsby';

import { HeroBannerContentfulModel, HeroBannerSectionContentfulModel } from 'src/api/models/HeroBanner';
import { PhoneCarouselContentfulModel, PhoneCarouselSectionContentfulModel } from '../api/models/PhoneCarousel';
import Layout from '../elements/layout/layout';
import BannerCourses from '../organisms/banners/banner.courses';
import CoursesCallToAction from 'organisms/courses/courses.callToAction'
import CoursesVideo from 'organisms/courses/courses.video'
import CoursesCarousel from 'src/molecules/courses/courses.carousel'
import { CaseStudiesSliderContentfulModel, CaseStudiesSliderSectionContentfulModel } from 'src/api/models/CaseStudy'
import CoursesStoriesOfAwesome from 'src/molecules/courses/courses.storiesOfAwesome'
import OverlayVideo from 'src/elements/video/overlay.video';
import PromotionModal from 'src/organisms/promotion/PromotionModal';
import { CoursesVideoSectionContentfulModel, LearnMoreBannerSectionContentfulModel } from 'src/api/models/Courses';
import { HeadingContentfulModel } from 'src/api/models/Heading';
import { ShortParagraphContentModel } from 'src/api/models/ShortParagraph';
import { ButtonContentfulModel } from 'src/api/models/Button';

interface LandingProps extends PageProps {
  data: {
    contentfulPage: {
      title: string;
      seoTitle: string;
      seoDescription: string;
      displayPromotionModal?: boolean;
      seoImage?: {
        file?: {
          url: string;
        }
      }
      contentfulchildren: (
        HeroBannerSectionContentfulModel |
        PhoneCarouselSectionContentfulModel |
        CaseStudiesSliderSectionContentfulModel |
        CoursesVideoSectionContentfulModel |
        LearnMoreBannerSectionContentfulModel
      )[];
    }
  };
}

const CoursePage = (props: LandingProps): ReactElement => {
  const [ videoModalIsOpened, setVideoModalIsOpened ] = useState(false);

  const helmetData = {
    title: props.data.contentfulPage.seoTitle,
    description: props.data.contentfulPage.seoDescription,
    previewImageURL: props.data.contentfulPage.seoImage?.file?.url
  }
  const displayModal = props.data.contentfulPage?.displayPromotionModal;

  // >> Banner sections
  const bannerSectionData = (props.data.contentfulPage.contentfulchildren as HeroBannerSectionContentfulModel[])
    .find(data => data.label.includes('Hero Banner Container Courses')).contentfulchildren;
  const bannerData: HeroBannerContentfulModel = bannerSectionData
    .find(data => data.internal?.type === 'ContentfulHeroBanner');
  // ---------------------------

  // >> Phone Carousel sections
  const phoneCarouselSectionData = (props.data.contentfulPage.contentfulchildren as PhoneCarouselSectionContentfulModel[])
    .find(data => data.label.includes('Phone Carousel Container')).contentfulchildren;
  const phoneCarouselProps = (phoneCarouselSectionData
    .find(data => data.internal?.type === 'ContentfulPhoneCarousel') as PhoneCarouselContentfulModel).contentfulchildren;
  const phoneCarouselTitle = phoneCarouselSectionData.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel;
  const phoneCarouselCallToActions = phoneCarouselSectionData.filter(data => data.internal.type === 'ContentfulButton') as ButtonContentfulModel[];
  // ---------------------------

  // >> Case Studies section
  const caseStudiesSectionData = (props.data.contentfulPage.contentfulchildren as CaseStudiesSliderSectionContentfulModel[])
    .find(data => data.label.includes('Courses Stories Container')).contentfulchildren;
  const caseStudies = (caseStudiesSectionData
    .find(data => data.internal?.type === 'ContentfulCaseStudiesSlider') as CaseStudiesSliderContentfulModel).contentfulchildren;
  // ---------------------------

  // >> Modal Video section
  const modalVideoSectionData = (props.data.contentfulPage.contentfulchildren as CoursesVideoSectionContentfulModel[])
    .find(data => data.label.includes('Mighty Courses Video Container')).contentfulchildren;
  const modalVideoTitle = modalVideoSectionData.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel;
  const modalVideoDescription = modalVideoSectionData.find(data => data.internal.type === 'ContentfulShortParagraph') as ShortParagraphContentModel;
  const modalVideoButtons = modalVideoSectionData.filter(data => data.internal.type === 'ContentfulButton') as ButtonContentfulModel[];
  const thumbnailButton = modalVideoButtons.find(button => button.variant.includes('thumbnail video'));
  const modalVideoCTAs = modalVideoButtons.filter(button => !button.variant.includes('thumbnail video'));
  // ---------------------------

  // >> Courses Call to Action section
  const learnMoreSectionData = (props.data.contentfulPage.contentfulchildren as LearnMoreBannerSectionContentfulModel[])
    .find(data => data.label.includes('Learn More Banner Container')).contentfulchildren;
  const callToActionHeadings = learnMoreSectionData.filter(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel[];
  const callToActionButton = learnMoreSectionData.find(data => data.internal.type === 'ContentfulButton') as ButtonContentfulModel;
  // ---------------------------

  const videoButton = bannerData.buttons.find(button => !!button.video)

  return (
    <Layout helmet={helmetData} location={props.location}>
      <BannerCourses
        {...{
          title: bannerData.title,
          description: bannerData.description,
          textPosition: 'right',
          removeRating: true,
          actionLink: {
            url: bannerData.buttons[ 0 ].url,
            text: bannerData.buttons[ 0 ].text,
            caption: bannerData.buttons[ 0 ].helpText || null
          },
          actionButton: {
            text: bannerData.buttons[ 1 ].text,
            onClick: () => setVideoModalIsOpened(true)
          },
          gatsbyImageData: bannerData.media.gatsbyImageData,
        }}
      />
      {
        videoButton && (
          <OverlayVideo
            overlayVisible={videoModalIsOpened}
            setOverlayVisible={setVideoModalIsOpened}
            src={videoButton.video.file.url}
          />
        )
      }
      <CoursesCallToAction
        actionButton={callToActionButton}
        titleHeadings={callToActionHeadings}
      />
      <CoursesVideo
        actionButtons={modalVideoCTAs}
        title={modalVideoTitle.text}
        description={modalVideoDescription.text}
        modalVideoButton={thumbnailButton}
      />
      <CoursesCarousel
        title={phoneCarouselTitle.text}
        images={phoneCarouselProps}
        actionButtons={phoneCarouselCallToActions}
      />

      <CoursesStoriesOfAwesome
        caseStudies={caseStudies}
      />
      <PromotionModal displayModal={displayModal}/>

    </Layout>
  );
};

// TODO: Arrange graphql into graph blocks and exports under src/api folder
export const pageQuery = graphql`
  query CoursesPageQuery {
    contentfulPage(contentful_id: {eq: "dA2Oauxx4tolA6gBzcjcx"}) {
      title
      seoTitle
      seoDescription
      seoImage {
        file {
          url
        }
      }
      id
      displayPromotionModal
      contentfulchildren {
        id
        label
        contentfulchildren {
          ...HeroBannerContent
          ...PhoneCarouselContent
          ...CaseStudiesSliderContent
          ...ButtonContent
          ...HeadingContent
          ...ShortParagraphContent
        }
      }
    }
  }
`;
export default CoursePage;
